import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

import logo from "@/assets/images/UrbNetLogoSemFundo.png";
import { useTheme } from "@/hooks/use-theme";
import { useToast } from "@/hooks/use-toast";
import { LandingAtmosphere } from "@/components/landing/LandingAtmosphere";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  leadService,
  type SolicitarContatoComercialRequest,
  TIPO_INSTITUICAO_OPCOES,
  CARGO_OPCOES,
  TAMANHO_CIDADE_OPCOES,
  MOTIVO_CONTATO_OPCOES,
} from "@/services/leadService";

const ESTADOS = [
  "Acre", "Alagoas", "Amapá", "Amazonas", "Bahia", "Ceará", "Distrito Federal",
  "Espírito Santo", "Goiás", "Maranhão", "Mato Grosso", "Mato Grosso do Sul",
  "Minas Gerais", "Pará", "Paraíba", "Paraná", "Pernambuco", "Piauí",
  "Rio de Janeiro", "Rio Grande do Norte", "Rio Grande do Sul", "Rondônia",
  "Roraima", "Santa Catarina", "São Paulo", "Sergipe", "Tocantins",
];

const initialForm: SolicitarContatoComercialRequest = {
  nome: "",
  email: "",
  celular: "",
  tipoInstituicao: "",
  empresa: "",
  cargo: "",
  estado: "",
  cidade: "",
  tamanhoCidade: "",
  motivoContato: "",
  mensagem: "",
};

export default function SolicitarOrcamentoPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Page is always light — restore previous theme on unmount
  const { theme, setTheme } = useTheme();
  const originalTheme = useRef(theme);
  useEffect(() => {
    if (theme !== "light") setTheme("light");
  }, [theme, setTheme]);
  useEffect(() => {
    return () => setTheme(originalTheme.current);
  }, [setTheme]);

  const [form, setForm] = useState<SolicitarContatoComercialRequest>(initialForm);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const setField = <K extends keyof SolicitarContatoComercialRequest>(
    field: K,
    value: SolicitarContatoComercialRequest[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validarLocal = (): boolean => {
    const obrigatorios: (keyof SolicitarContatoComercialRequest)[] = [
      "nome", "email", "celular", "tipoInstituicao", "empresa",
      "cargo", "estado", "cidade", "tamanhoCidade", "motivoContato",
    ];
    const novos: Record<string, boolean> = {};
    obrigatorios.forEach((campo) => {
      if (!String(form[campo] ?? "").trim()) novos[campo] = true;
    });
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      novos.email = true;
    }
    setErrors(novos);
    return Object.keys(novos).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validarLocal()) {
      toast({
        title: "Preencha todos os campos obrigatórios",
        description: "Todos os campos marcados são necessários para solicitar o contato comercial.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    const result = await leadService.solicitarContatoComercial(form);
    setIsSubmitting(false);

    if ("failure" in result) {
      toast({
        title: "Não foi possível enviar",
        description: result.failure.messages?.join(" ") || "Tente novamente em instantes.",
        variant: "destructive",
      });
      return;
    }

    setEnviado(true);
    toast({
      title: "Solicitação enviada!",
      description: "Nossa equipe comercial entrará em contato em breve.",
    });
  };

  const fieldClass = (field: keyof SolicitarContatoComercialRequest) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  if (enviado) {
    return (
      <div className="relative isolate flex min-h-screen items-center justify-center overflow-x-hidden bg-transparent px-6 text-foreground">
        <LandingAtmosphere />
        <div className="relative w-full max-w-md rounded-3xl border border-white/60 bg-white/80 p-10 text-center shadow-[0_18px_48px_-32px_hsl(var(--foreground)/0.35)] backdrop-blur">
          <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h1 className="font-heading text-2xl font-bold">Solicitação enviada!</h1>
          <p className="mt-3 text-muted-foreground">
            Recebemos seus dados. Nossa equipe comercial entrará em contato em breve
            para apresentar a melhor solução para sua gestão.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
          >
            Voltar para o início
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden bg-transparent text-foreground">
      <LandingAtmosphere />

      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/40 bg-background/50 backdrop-blur md:backdrop-blur-2xl">
        <div className="relative mx-auto flex h-[80px] max-w-7xl items-center justify-between px-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
          <a href="/" className="flex items-center gap-3">
            <img src={logo} alt="UrbNet" className="h-10 w-auto" />
            <span className="hidden font-heading text-lg font-bold tracking-tight text-foreground sm:inline">
              UrbNet Ouvidoria
            </span>
          </a>
          <div className="w-[64px]" />
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-2xl px-6 pt-[120px] pb-24">
        <div className="mb-8 text-center">
          <h1 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">
            Solicite seu orçamento
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Preencha os dados abaixo e nossa equipe comercial entrará em contato para
            apresentar a solução ideal para a sua gestão.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="rounded-3xl border border-white/60 bg-white/80 p-6 shadow-[0_18px_48px_-32px_hsl(var(--foreground)/0.3)] backdrop-blur sm:p-10"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Nome */}
            <div className="sm:col-span-2">
              <Label htmlFor="nome">Nome completo *</Label>
              <Input
                id="nome"
                value={form.nome}
                onChange={(e) => setField("nome", e.target.value)}
                placeholder="Seu nome"
                maxLength={256}
                className={`mt-1.5 rounded-xl ${fieldClass("nome")}`}
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="voce@exemplo.com"
                maxLength={256}
                className={`mt-1.5 rounded-xl ${fieldClass("email")}`}
              />
            </div>

            {/* Celular */}
            <div>
              <Label htmlFor="celular">Celular *</Label>
              <Input
                id="celular"
                value={form.celular}
                onChange={(e) => setField("celular", e.target.value)}
                placeholder="(00) 00000-0000"
                maxLength={50}
                className={`mt-1.5 rounded-xl ${fieldClass("celular")}`}
              />
            </div>

            {/* Tipo de instituição */}
            <div>
              <Label>Tipo de instituição *</Label>
              <Select
                value={form.tipoInstituicao}
                onValueChange={(v) => setField("tipoInstituicao", v as typeof form.tipoInstituicao)}
              >
                <SelectTrigger className={`mt-1.5 rounded-xl ${fieldClass("tipoInstituicao")}`}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {TIPO_INSTITUICAO_OPCOES.map((op) => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Empresa / órgão */}
            <div>
              <Label htmlFor="empresa">Empresa / Órgão *</Label>
              <Input
                id="empresa"
                value={form.empresa}
                onChange={(e) => setField("empresa", e.target.value)}
                placeholder="Nome da instituição"
                maxLength={256}
                className={`mt-1.5 rounded-xl ${fieldClass("empresa")}`}
              />
            </div>

            {/* Cargo */}
            <div>
              <Label>Cargo *</Label>
              <Select
                value={form.cargo}
                onValueChange={(v) => setField("cargo", v as typeof form.cargo)}
              >
                <SelectTrigger className={`mt-1.5 rounded-xl ${fieldClass("cargo")}`}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {CARGO_OPCOES.map((op) => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estado */}
            <div>
              <Label>Estado *</Label>
              <Select
                value={form.estado}
                onValueChange={(v) => setField("estado", v)}
              >
                <SelectTrigger className={`mt-1.5 rounded-xl ${fieldClass("estado")}`}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS.map((uf) => (
                    <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cidade */}
            <div>
              <Label htmlFor="cidade">Cidade *</Label>
              <Input
                id="cidade"
                value={form.cidade}
                onChange={(e) => setField("cidade", e.target.value)}
                placeholder="Sua cidade"
                maxLength={256}
                className={`mt-1.5 rounded-xl ${fieldClass("cidade")}`}
              />
            </div>

            {/* Tamanho da cidade */}
            <div className="sm:col-span-2">
              <Label>Tamanho da cidade *</Label>
              <Select
                value={form.tamanhoCidade}
                onValueChange={(v) => setField("tamanhoCidade", v as typeof form.tamanhoCidade)}
              >
                <SelectTrigger className={`mt-1.5 rounded-xl ${fieldClass("tamanhoCidade")}`}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {TAMANHO_CIDADE_OPCOES.map((op) => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Motivo do contato */}
            <div className="sm:col-span-2">
              <Label>Motivo do contato *</Label>
              <Select
                value={form.motivoContato}
                onValueChange={(v) => setField("motivoContato", v as typeof form.motivoContato)}
              >
                <SelectTrigger className={`mt-1.5 rounded-xl ${fieldClass("motivoContato")}`}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVO_CONTATO_OPCOES.map((op) => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mensagem (opcional) */}
            <div className="sm:col-span-2">
              <Label htmlFor="mensagem">Mensagem (opcional)</Label>
              <Textarea
                id="mensagem"
                value={form.mensagem}
                onChange={(e) => setField("mensagem", e.target.value)}
                placeholder="Conte-nos mais sobre sua necessidade"
                maxLength={4000}
                rows={4}
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              backgroundImage:
                "linear-gradient(135deg, hsl(var(--secondary)) 0%, hsl(var(--primary)) 100%)",
            }}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.08em] text-primary-foreground shadow-[0_10px_35px_-10px_hsl(var(--primary)/0.65)] ring-2 ring-white/35 transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                Enviar solicitação
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </form>
      </main>
    </div>
  );
}
